import classNames from "classnames";
import * as React from "react";

import { AppSidebarStorage } from "@/shared/state/appSidebar";

import { ResizeHandle } from "./ResizeHandle";
import styles from "./Sidebar.module.css";

interface SidebarProps extends React.PropsWithChildren {
  /** Whether the sidebar is open. */
  isOpen: boolean;
}

/**
 * A resizable sidebar component used to display
 * the app navigation.
 */
export function Sidebar(props: SidebarProps) {
  const sidebarRef = React.useRef<HTMLElement>(null);

  const onResize = React.useCallback((deltaX: number) => {
    if (!sidebarRef.current) {
      return;
    }

    const newWidthPx = sidebarRef.current.offsetWidth + deltaX;

    sidebarRef.current.style.width = `${newWidthPx}px`;
    AppSidebarStorage.setWidth(newWidthPx);
  }, []);

  React.useEffect(function initializeWidthFromLocalStorage() {
    if (!sidebarRef.current) {
      return;
    }

    const savedWidth = AppSidebarStorage.getWidth();
    if (savedWidth !== null) {
      // The value is gated by minWidth and maxWidth in CSS
      sidebarRef.current.style.width = `${savedWidth}px`;
    }
  }, []);

  return (
    <aside
      className={classNames(styles.sidebar, {
        [styles.closed]: !props.isOpen,
      })}
      ref={sidebarRef}
    >
      {props.children}
      <ResizeHandle onResize={onResize} />
    </aside>
  );
}
